import { debounce } from 'lodash';
import React, { FC, useState } from 'react';
import { Control, FieldError, useController } from 'react-hook-form';
import { SearchIcon } from 'static';

import EmojiPicker from '../EmojiArea/EmojiPicker';
import { StyledIconsBlock, StyledLeftIcon } from '../SearchInput/styled';
import { StyledErrorMessage, StyledLabel, StyledWrapper } from '../styled';
import {
  HidePasswordIcon,
  InputWrapper,
  LabelWrapper,
  ShowPasswordButton,
  ShowPasswordIcon,
  StyledInput,
} from './styled';

interface TextInputV2Props {
  name: string;
  type?: 'text' | 'number' | 'password';
  placeholder?: string;
  disabled?: boolean;
  readonly?: boolean;
  label?: string;
  error?: FieldError;
  register: any;
  width?: string;
  defaultValue?: string | number;
  pattern?: string;
  min?: string;
  withShowIcon?: boolean;
  withSearchIcon?: boolean;
  control: Control;
  height?: string;
  extraWrapperStyle?: { [key: string]: string };
  maxLength?: number;
  onBlur?: () => void;
  labelFontSize?: string | number;
  labelFontWeight?: string | number;
}

const TextInputEmoji: FC<TextInputV2Props> = ({
  name,
  type = 'text',
  placeholder,
  disabled = false,
  readonly = false,
  label,
  error,
  register,
  width = '100%',
  defaultValue,
  min,
  pattern,
  withShowIcon,
  withSearchIcon,
  onBlur = () => {},
  control,
  height,
  extraWrapperStyle,
  maxLength,
  labelFontSize,
  labelFontWeight,
}) => {
  const [inputType, setType] = useState<'text' | 'password' | 'number'>(type);
  const [cursorPosition, setCursorPosition] = useState<number>(0);
  const { field } = useController({
    name,
    control,
  });

  const togglePasswordVisibility = () =>
    inputType === 'text' ? setType('password') : setType('text');

  const setCursorLocation = debounce(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCursorPosition(e.target.selectionStart);
    },
    250,
  );

  return (
    <StyledWrapper>
      {label && (
        <LabelWrapper>
          <StyledLabel htmlFor={name} error={error}>
            {label}
          </StyledLabel>
          {maxLength && (
            <StyledLabel
              htmlFor={name}
              error={error}
              fontSize={labelFontSize}
              fontWeight={labelFontWeight}
            >
              ({field.value.length} / {maxLength})
            </StyledLabel>
          )}
        </LabelWrapper>
      )}
      <InputWrapper
        style={extraWrapperStyle ? extraWrapperStyle : {}}
        position="relative"
      >
        <StyledInput
          height={height}
          width={width}
          id={name}
          type={inputType}
          disabled={disabled}
          readOnly={readonly}
          placeholder={placeholder}
          error={error}
          name={name}
          {...register}
          min={min || null}
          pattern={pattern || null}
          defaultValue={defaultValue}
          onBlur={onBlur}
          value={field.value}
          onChange={field.onChange}
          onSelect={setCursorLocation}
          withShowIcon={withShowIcon}
          maxLength={maxLength}
        />
        <EmojiPicker
          positionTop="2px"
          positionRight="4px"
          cursorPosition={cursorPosition}
          setCursorPosition={setCursorPosition}
          setValue={field.onChange}
          value={field.value}
        />
        {withSearchIcon && (
          <StyledIconsBlock>
            <StyledLeftIcon src={SearchIcon} alt="" />
          </StyledIconsBlock>
        )}
        {type === 'password' && withShowIcon ? (
          <ShowPasswordButton
            type="button"
            onClick={togglePasswordVisibility}
            tabIndex="-1"
          >
            {inputType === 'password' ? (
              <ShowPasswordIcon />
            ) : (
              <HidePasswordIcon />
            )}
          </ShowPasswordButton>
        ) : null}
      </InputWrapper>
      {error && <StyledErrorMessage>{error.message}</StyledErrorMessage>}
    </StyledWrapper>
  );
};

export default TextInputEmoji;
